import * as Backbone from "backbone";
import * as ko from "knockout";
import "knockout.validation";
import * as _ from "lodash";
import * as moment from "moment";
import * as Global from "../common/global";
import * as AuthenticationData from "../data/authenticationData";
import { IApplication } from "../interfaces/application";
import { ITemplates } from "../interfaces/controller";
import { IResponse } from "../interfaces/data";
import * as User from "../interfaces/user";
import * as AppHost from "./appHost";
import * as Constants from "./constants";
import { showPasswordDialog } from "./enterPasswordDialog";
import * as Navigation from "./navigation";
import * as PageManagerModule from "./pageManager";
import { SAMLLoginManager } from "./saml";
import { Templates } from "./templates";
import * as UI from "./ui";
import * as Util from "./util";

export class Application implements IApplication {

    // global values loaded when web site first is loaded
    public global: User.IAppGlobal;

    // flag to indicate if the user is logged in
    public isLoggedIn: boolean = false;

    // web service base URL
    public webServiceBaseURL: string;
    public webServiceBaseDomain: string;
    public reportingWebServiceBaseURL: string;

    // information about the currently logged in user
    public userSession: User.IUserSession | null = null;

    // a token used to authenticate any future web service requests
    public authenticationToken: string | null = null;
    public reportToken: string | null = null;

    // If this flag is true, then hide all headers, footers, menus and tabs.
    // Used when OnCue is embedded into another web applications
    public hideMenu: boolean = false;

    private templatesSingleton: ITemplates;
    // backbone components that handles URL changes in the browser
    private routerSingleton: AppHost.AppRouter;

    // component that manages all open pages and the display of
    // the open pages.
    private pageManagerSingleton: PageManagerModule.PageManager;

    // controls the navigation bar/menu at the top of the page
    private navigationBar: Navigation.NavigationBar;

    private eventsSingleton: any;
    private sessionExpirationTimer: number | null = null;

    private samlLoginManager: SAMLLoginManager;

    // login a user and set any user fields
    public loginUser(authenticationToken: string, userSession: User.IUserSession, reportToken?: string): void {
        this.setUserSession(userSession);
        this.setAuthenticationToken(authenticationToken);
        this.setReportToken(reportToken);
        this.isLoggedIn = true;
        this.pageManagerSingleton.login();

        this.navigationBar.load(userSession);
    }

    public setUserSession(userSession: User.IUserSession): void {
        this.userSession = userSession;
    }

    public setAuthenticationToken(authenticationToken: string): void {
        this.authenticationToken = authenticationToken;

        // save AuthenticationToken into a cookie, in case
        // the users does a hard refresh of the page, we can
        // then log them back in
        Util.setCookie(Constants.COOKIE_AUTHENTICATIONTOKEN, authenticationToken);

        // Set up a timer to keep track of when the token expires.
        if (this.sessionExpirationTimer) {
            clearTimeout(this.sessionExpirationTimer);
        }

        if (this.global.SessionTimeoutMinutes > this.global.SessionTimeoutWarningMinutes) {
            // Set timer to X minutes prior to expiration, to warn user the session is about to expire.
            const waitTimeInMilliseconds = (this.global.SessionTimeoutMinutes - this.global.SessionTimeoutWarningMinutes) * 60 * 1000;
            this.sessionExpirationTimer = window.setTimeout(() => {
                this.sessionExpirationTimer = null;
                UI.showSessionTimeWarningDialog();
            }, waitTimeInMilliseconds);
        }
    }

    public logoutUser(): void {
        this.userSession = null;
        this.isLoggedIn = false;
        Util.setCookie(Constants.COOKIE_AUTHENTICATIONTOKEN, "");
        Util.setCookie(Constants.COOKIE_REPORTAUTHENTICATIONTOKEN, "");

        if (this.sessionExpirationTimer) {
            clearTimeout(this.sessionExpirationTimer);
            this.sessionExpirationTimer = null;
        }

        if (this.global.AuthenticationMethod === "SAML") {
            this.samlLoginManager.logout();
        } else {
            // force a full page reload, just to make sure all data is cleared
            window.location.href = "/";
        }
    }

    public templates(): ITemplates {
        if (this.templatesSingleton == null) {
            this.templatesSingleton = new Templates();
        }
        return this.templatesSingleton;
    }

    public events(): Backbone.Events {
        if (this.eventsSingleton == null) {
            // use the Backbone Events class to handle event aggregation
            this.eventsSingleton = _.clone(Backbone.Events);
        }
        return this.eventsSingleton;
    }

    public router(): AppHost.AppRouter {
        return this.routerSingleton;
    }

    public pageManager(): PageManagerModule.PageManager {
        return this.pageManagerSingleton;
    }

    public showErrorMessage(messages: string[]): void {
        UI.showErrorMessage(messages);
    }

    public showPasswordDialog(): void {
        showPasswordDialog();
    }

    public redirectToExternalLoginIfRequired(): boolean {
        if (this.isLoggedIn) {
            // already logged in, nothing to do
            return false;
        }
        if (window.location.pathname && window.location.pathname.indexOf("Login") >= 0) {
            // trying to open login page, so let it happen
            return false;
        }

        if (this.global.AuthenticationMethod === "SAML") {
            if (this.samlLoginManager.isCurrentPageSAMLLogin()) {
                // User has authenticated via the SAML process, so continue loading the SAMLLogin page
                // to finish logging into the OnCue site.
                return false;
            } else {
                // User is not logged in, so redirect to the SAML login page
                this.samlLoginManager.login();
                return true;
            }
        }
        // else, no external login being used, so just continue normal processing
        // and display home page with login fields
        return false;
    }

    // does any first time setup for the application
    public init(): JQueryPromise<any> {
        const me: Application = this;
        const authenticationToken: string = Util.getCookie(Constants.COOKIE_AUTHENTICATIONTOKEN);
        const reportToken: string = Util.getCookie(Constants.COOKIE_REPORTAUTHENTICATIONTOKEN);
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        // must call this before doing any web service calls
        this.setWebServiceBaseURLs();

        this.checkForHideMenuQueryParameter();

        // Globally limit the filter options in kendo grids
        if (kendo.ui.FilterMenu.fn.options.operators) {
            kendo.ui.FilterMenu.fn.options.operators.string = {
                eq: "Is equal to",
                neq: "Is not equal to",
                startswith: "Starts with",
                endswith: "Ends with",
                contains: "Contains",
                doesnotcontain: "Does not contain",
            };

            kendo.ui.FilterMenu.fn.options.operators.date = {
                eq: "Is equal to",
                neq: "Is not equal to",
                lt: "Is before",
                lte: "Is before or equal to",
                gt: "Is after",
                gte: "Is after or equal to",
            };

            kendo.ui.FilterMenu.fn.options.operators.number = {
                eq: "Is equal to",
                neq: "Is not equal to",
                lt: "Is less than",
                lte: "Is less than or equal to",
                gt: "Is greater than",
                gte: "Is greater than or equal to",
            };
        }

        Util.callWebService(
            "/api/App/AppLoadValues",
            "GET")
            .done((response: IResponse<User.IAppGlobal>) => {
                if (response.Success) {
                    me.global = response.Payload;
                    me.initExternalLoginHandlers(response.Payload);
                }

                // start component that handles URL changes.  Note below router.StartRouter()
                // must be called for the router to start processing URL requests.  But this
                // must be done after logging in the user, if they have a good token
                me.routerSingleton = new AppHost.AppRouter();

                // create component that manages all open pages
                me.pageManagerSingleton = new PageManagerModule.PageManager();

                // top navigation/menu bar
                me.navigationBar = new Navigation.NavigationBar();

                me.createKOValidationRules();
                me.createKOBindingHandlers();

                // if an error happens, just continue processing
                // this may have to change in the future if there is a critical value
                // that must be loaded before the application can continue.
            })
            .always(() => {
                if (!Util.isNullOrEmpty(authenticationToken)) {
                    // a previous token exists, try to use it to login the user
                    AuthenticationData.AuthenticateUserWithToken({ Token: authenticationToken })
                        .done((response: IResponse<AuthenticationData.IAuthenticateUserWithTokenResponse>) => {
                            if (response.Success && response.Payload.IsAuthenticated) {
                                // token is good, so login user
                                me.loginUser(response.Payload.AuthenticationToken, response.Payload.UserSession, reportToken);
                            }
                        })
                        .always(() => {
                            if (!me.redirectToExternalLoginIfRequired()) {
                                // start processing URL requests
                                me.routerSingleton.startRouter();
                            }
                            deferred.resolve();
                        });

                } else {
                    if (!me.redirectToExternalLoginIfRequired()) {
                        // start processing URL requests
                        me.routerSingleton.startRouter();
                    }
                    deferred.resolve();
                }
            });

        return deferred.promise();
    }

    // determines the URL to use for the web service
    private setWebServiceBaseURLs(): void {
        switch (window.location.host.toLowerCase()) {
            case "localhost:8080":
                this.reportingWebServiceBaseURL = "http://localhost:39922";
                this.webServiceBaseURL = "http://localhost:39921";
                this.webServiceBaseDomain = "localhost";
                break;

            case "portaldev.oncue360dev.com":
                this.reportingWebServiceBaseURL = "https://reportservicedev.oncue360dev.com";
                this.webServiceBaseURL = "https://webservicedev.oncue360dev.com";
                this.webServiceBaseDomain = "oncue360dev.com";
                break;

            case "portalqa.oncue360dev.com":
                this.reportingWebServiceBaseURL = "https://reportserviceqa.oncue360dev.com";
                this.webServiceBaseURL = "https://webserviceqa.oncue360dev.com";
                this.webServiceBaseDomain = "oncue360dev.com";
                break;

            case "portaldemo.oncue360dev.com":
                this.reportingWebServiceBaseURL = "https://reportservicedemo.oncue360dev.com";
                this.webServiceBaseURL = "https://webservicedemo.oncue360dev.com";
                this.webServiceBaseDomain = "oncue360dev.com";
                break;

            case "portalcsap.oncue360dev.com":
                this.reportingWebServiceBaseURL = "https://reportservicecsap.oncue360dev.com";
                this.webServiceBaseURL = "https://webservicecsap.oncue360dev.com";
                this.webServiceBaseDomain = "oncue360dev.com";
                break;

            case "www.cgioncue360.ca":
                this.reportingWebServiceBaseURL = "https://reportservice.cgioncue360.ca";
                this.webServiceBaseURL = "https://webservice.cgioncue360.ca";
                this.webServiceBaseDomain = "cgioncue360.ca";
                break;

            case "www.wvgrantstest.com":
                this.reportingWebServiceBaseURL = "https://reportservice.wvgrantstest.com";
                this.webServiceBaseURL = "https://webservice.wvgrantstest.com";
                this.webServiceBaseDomain = "wvgrantstest.com";
                break;

            case "www.findwvgrants.com":
                this.reportingWebServiceBaseURL = "https://reportservice.findwvgrants.com";
                this.webServiceBaseURL = "https://webservice.findwvgrants.com";
                this.webServiceBaseDomain = "findwvgrants.com";
                break;

            case "grantsapp.wv.gov":
                this.reportingWebServiceBaseURL = "https://grantsappnfo.wv.gov";
                this.webServiceBaseURL = "https://grantsappapi.wv.gov";
                this.webServiceBaseDomain = "wv.gov";
                break;
        }
    }

    // add any validation rules to knockout
    private createKOBindingHandlers(): void {

        ko.bindingHandlers.bootstrapShowModal = {
            update(element: JQuery<HTMLElement>, valueAccessor: any): void {
                const value: any = valueAccessor();
                if (ko.utils.unwrapObservable(value)) {
                    $(element).modal("show");
                    // this is to focus input field inside dialog
                    $("input", element).focus();
                } else {
                    $(element).modal("hide");
                }
            },
        };

        ko.bindingHandlers.slideVisible = {
            update(element: JQuery<HTMLElement>, valueAccessor: any, allBindingsAccessor: KnockoutAllBindingsAccessor): void {
                // first get the latest data that we're bound to
                const value: any = valueAccessor();
                const allBindings: any = allBindingsAccessor();

                // next, whether or not the supplied model property is observable, get its current value
                const valueUnwrapped: any = ko.utils.unwrapObservable(value);

                // grab some more data from another binding property
                const duration: number = allBindings.slideDuration || 300; // 400ms is default duration unless otherwise specified

                // now manipulate the DOM element
                if (valueUnwrapped === true) {
                    $(element).slideDown(duration); // make the element visible
                } else {
                    $(element).slideUp(duration);   // make the element invisible
                }
            },
        };
    }

    // add any validation rules to knockout
    private createKOValidationRules(): void {
        ko.validation.init({
            registerExtenders: true,
            messagesOnModified: true,
            decorateElementOnModified: true,
            insertMessages: true,
            errorMessageClass: "alert alert-warning",
            decorateInputElement: true,
            errorClass: "has-error",
            parseInputAttributes: true,
            messageTemplate: "master_fieldValidationTemplate",
            grouping: { deep: true },
        }, true);

        ko.validation.rules.mustEqual = {
            validator(val: any, otherVal: any): boolean {
                return val === otherVal;
            },
            message: "The field must equal {0}",
        };

        const isDateTimeValid = (date: any, time: any, dateTime: any): boolean => {
            const app: IApplication = Global.app();
            const customerSettings = app.userSession ? app.userSession.CustomerSettings : [];
            const dateFormat = UI.GetCustomerSetting(customerSettings, Constants.SETTING_KEY_DATEFORMAT, Constants.DEFAULT_DATE_FORMAT);
            const timeFormat = UI.GetCustomerSetting(customerSettings, Constants.SETTING_KEY_TIMEFORMAT, Constants.DEFAULT_TIME_FORMAT);
            const momentDateFormat = UI.getMomentDateFormat(dateFormat);
            const momentTimeFormat = UI.getMomentTimeFormat(timeFormat);

            const dateTimeString = dateTime ? dateTime : `${date ? date : moment().format(momentDateFormat)} ${time ? time : moment().format(momentTimeFormat)}`;
            const dateTimeMoment = moment(dateTimeString, `${momentDateFormat} ${momentTimeFormat}`, true);
            return dateTimeMoment.isValid();
        };

        ko.validation.rules.dateValid = {
            validator(val: any): boolean {
                return isDateTimeValid(val, null, null);
            },
            message: "The date is not valid",
        };

        ko.validation.rules.timeValid = {
            validator(val: any): boolean {
                return isDateTimeValid(null, val, null);
            },
            message: "The time is not valid",
        };

        ko.validation.rules.dateTimeValid = {
            validator(val: any): boolean {
                return isDateTimeValid(null, null, val);
            },
            message: "The date/time is not valid",
        };

        ko.validation.registerExtenders();
    }

    private setReportToken(reportToken: string | undefined): void {
        if (reportToken) {
            this.reportToken = reportToken;
            Util.setCookie(Constants.COOKIE_REPORTAUTHENTICATIONTOKEN, reportToken);
        }
    }

    private initExternalLoginHandlers(appConfig: User.IAppGlobal) {
        if (appConfig.AuthenticationMethod === "SAML") {
            appConfig.SelfRegistrationAllowed = false;
            this.samlLoginManager = new SAMLLoginManager({
                loginPageURL: appConfig.AuthenticationExternalWebURL,
                logoutPageURL: appConfig.AuthenticationExternalLogoutWebURL,
            });
        }
    }

    private checkForHideMenuQueryParameter() {
        const hideMenu = Util.getURLParameter("hidemenu");
        if (hideMenu.toLowerCase() === "true") {
            this.hideMenu = true;
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/application.ts